import { type ImgHTMLAttributes, type CSSProperties } from "react";

/**
 * En enskild bredd-variant av samma bild på olika upplösningar.
 * Använd när ni laddat upp flera storlekar som separata assets.
 *   sources={[
 *     { url: "/...hero-480.webp",  width: 480  },
 *     { url: "/...hero-960.webp",  width: 960  },
 *     { url: "/...hero-1920.webp", width: 1920 },
 *   ]}
 */
export interface ImgSource {
  url: string;
  width: number;
}

type Priority = "lcp" | "above-fold" | "lazy";

export interface ResponsiveImgProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet" | "loading" | "fetchPriority"> {
  /** Standardbild (fallback i `src`). Om `sources` saknas används bara den här. */
  src: string;
  /** Flera bredd-varianter av samma bild — genererar `srcset` automatiskt. */
  sources?: ImgSource[];
  /**
   * `sizes`-attribut. Default: "100vw". Sätt t.ex. "(min-width: 1024px) 50vw, 100vw"
   * för en bild som tar halva skärmen på desktop.
   */
  sizes?: string;
  /**
   * - "lcp": hero/första bilden over the fold — laddas eagerly med fetchpriority="high".
   * - "above-fold": viktig men inte LCP — eager, normal prioritet.
   * - "lazy" (default): laddas när den närmar sig viewporten.
   */
  priority?: Priority;
  /** Intrinsic bredd i pixlar — sätter `width`-attributet och reserverar aspekten. */
  width?: number;
  /** Intrinsic höjd i pixlar — sätter `height`-attributet och reserverar aspekten. */
  height?: number;
}

/**
 * Bild-komponent med inbyggda prestanda-defaults:
 *   - `decoding="async"` alltid
 *   - `loading="lazy"` som default (eller "eager" för LCP/above-fold)
 *   - `fetchpriority="high"` för LCP-bilden
 *   - `width`/`height` reserverar aspekten och eliminerar CLS
 *   - Genererar `srcset` när flera `sources` skickas in
 *
 * Lovables asset-CDN serverar bara originalfilen — vill ni ha äkta srcset
 * måste varje breddvariant laddas upp som en egen asset först.
 */
export function ResponsiveImg({
  src,
  sources,
  sizes = "100vw",
  priority = "lazy",
  width,
  height,
  style,
  alt,
  ...rest
}: ResponsiveImgProps) {
  const isLcp = priority === "lcp";
  const eager = priority !== "lazy";

  const srcSet = sources && sources.length > 0
    ? sources
        .slice()
        .sort((a, b) => a.width - b.width)
        .map((s) => `${s.url} ${s.width}w`)
        .join(", ")
    : undefined;

  const aspectStyle: CSSProperties | undefined =
    width && height ? { aspectRatio: `${width} / ${height}` } : undefined;

  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt={alt ?? ""}
      width={width}
      height={height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={isLcp ? "high" : undefined}
      style={{ ...aspectStyle, ...style }}
      {...rest}
    />
  );
}
